/* eslint-disable jsx-a11y/label-has-for */
import React from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';

import { ReactTemplate } from '@bingads-webui-react/react-template';
import { Checkbox } from '@bingads-webui-react/checkbox';

import { ActionSelector } from './action-selector';

import {
  stringKeys,
  actionOptions,
  hashOptions,
  customerMatchTermsLink,
  microsoftAdvertisingAgreementLink,
  microsoftAdvertisingPolicyLink,
} from '../common/constants';

import { CustomerListFile } from '../common/customer-list-file';
import { UploadSelector } from './upload-selector';

const panelTitleLabelStyle = { textAlign: 'right' };
const detailPanelStyle = { marginLeft: '10px', marginBottom: '10px' };

export class AudienceDetailPanel extends React.PureComponent {
  static propTypes = {
    i18n: PropTypes.shape({
      getString: PropTypes.func.isRequired,
      parseDecimal: PropTypes.func.isRequired,
    }).isRequired,
    isEditMode: PropTypes.bool.isRequired,
    onActionRequirementChanged: PropTypes.func.isRequired,
    onActionSelectionChanged: PropTypes.func.isRequired,
    onHashSelectionChanged: PropTypes.func.isRequired,
    onFileChangedAsync: PropTypes.func.isRequired,

    permissions: PropTypes.shape({
      IsCustomerListAudienceAllowed: PropTypes.bool.isRequired,
      IsCRMIdForCustomerListAudienceAllowed: PropTypes.bool.isRequired,
    }).isRequired,

    templatePath: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    // Only for test purpose
    if (this.props.testRef) {
      this.props.testRef.current = {
        that: this,
      };
    }
  }

  state = {
    isActionRequired: false,
    showActionError: false,
    consentStatus: false,
    showConsentError: false,
  };

  onActionRequirementChanged = (isActionRequired) => {
    this.setState({
      isActionRequired,
    });

    this.props.onActionRequirementChanged(isActionRequired);
  }

  onActionSelectionChanged = (s) => {
    this.actionSelection = s;
    this.props.onActionSelectionChanged(s);
  }

  onHashSelectionChanged = (s) => {
    this.hashSelection = s;
    this.props.onHashSelectionChanged(s);
  }

  onFileChangedAsync = (fileObject) => {
    this.isFileValid = false;
    this.props.onFileChangedAsync(null);

    if (!fileObject) {
      return Promise.resolve();
    }

    const fileConfig = {
      isCRMIdAllowed: this.props.permissions.IsCRMIdForCustomerListAudienceAllowed,
      isMSIDAllowed: this.props.permissions.IsAmplifyEnabled && this.props.permissions.dynamic.BoostConversionBasedSegment,
      isFileHashed: this.hashSelection.isHashed,
    };
    const customerListFile = new CustomerListFile(fileObject, fileConfig);

    return customerListFile.parseFileToObjectAsync()
      .then(() => {
        customerListFile.parseMetadata();
      })
      .then(() => {
        this.isFileValid = true;
        this.props.onFileChangedAsync(customerListFile);
      });
  }

  onConsentStatusChanged = (event) => {
    this.setState({
      consentStatus: event.target.checked,
    });
  }

  // Not states
  actionSelection = actionOptions.noop;
  hashSelection = hashOptions.plainText;
  isFileValid = false;

  validate = () => {
    const isFileAndConsentNeeded = !this.props.isEditMode
      || (this.props.isEditMode && this.state.isActionRequired);

    let hasFileError = false;
    let hasActionError = false;
    let hasConsentError = false;

    // Validate file and consent
    if (isFileAndConsentNeeded) {
      if (!this.isFileValid) {
        hasFileError = true;
      }

      if (!this.state.consentStatus) {
        hasConsentError = true;
      }
    }

    // Validate action selection
    if (this.props.isEditMode &&
      this.state.isActionRequired &&
      this.actionSelection === actionOptions.noop) {
      hasActionError = true;
    }

    this.setState({
      showActionError: hasActionError,
      showFileError: hasFileError,
      showConsentError: hasConsentError,
    });

    return !(hasActionError || hasFileError || hasConsentError);
  }

  render() {
    const actionSelector = (
      <div>
        <ActionSelector
          i18n={this.props.i18n}
          onActionRequirementChanged={this.onActionRequirementChanged}
          onActionSelectionChanged={this.onActionSelectionChanged}
        />
        <div className="error action-selector-error">
          {this.state.showActionError && this.state.isActionRequired ?
            this.props.i18n.getString(stringKeys.actionSelectorError) : null}
        </div>
      </div>
    );

    const uploadSelector = (
      <div>
        <UploadSelector
          i18n={this.props.i18n}
          onFileChangedAsync={this.onFileChangedAsync}
          onHashSelectionChanged={this.onHashSelectionChanged}
          templatePath={this.props.templatePath}
        />
        <div className="error upload-selector-error">
          {this.state.showFileError ?
            this.props.i18n.getString(stringKeys.fileError) : null}
        </div>
      </div>
    );

    const consentLine = (
      <div className="consent-line">
        <Checkbox
          inline
          checked={this.state.consentStatus}
          name="consent-checkbox"
          onChange={this.onConsentStatusChanged}
        >
          <ReactTemplate
            model={{
              termsLink: (
                <a href={customerMatchTermsLink} target="_blank" rel="noopener noreferrer">
                  {this.props.i18n.getString('Item_CustomerMatchTerms')}
                </a>
              ),
              agreementLink: (
                <a href={microsoftAdvertisingAgreementLink} target="_blank" rel="noopener noreferrer">
                  {this.props.i18n.getString('Item_MicrosoftAdvertisingAgreement')}
                </a>
              ),
              policyLink: (
                <a href={microsoftAdvertisingPolicyLink} target="_blank" rel="noopener noreferrer">
                  {this.props.i18n.getString('Item_MicrosoftAdvertisingPolicies')}
                </a>
              ),
            }}
            template={this.props.i18n.getString(stringKeys.consentOath)}
          />
        </Checkbox>
        <div className="error consent-line-error">
          {this.state.showConsentError ?
            this.props.i18n.getString(stringKeys.consentError) : null}
        </div>
        <div>
          <ReactTemplate
            model={{
              termsLink: (
                <a href={customerMatchTermsLink} target="_blank" rel="noopener noreferrer">
                  {this.props.i18n.getString('Item_CustomerMatchTerms')}
                </a>
              ),
            }}
            template={this.props.i18n.getString(stringKeys.consentStatement)}
          />
        </div>
      </div>
    );

    return (
      <div className="customer-list-detail-panel row">
        <label className="col-sm-2 control-label" style={panelTitleLabelStyle}>{this.props.i18n.getString(stringKeys.detailPanelTitle)}</label>
        <div className="col-sm-8" style={detailPanelStyle}>
          {
            this.props.isEditMode ? actionSelector : null
          }
          {
            (!this.props.isEditMode || (this.props.isEditMode && this.state.isActionRequired)) ?
              uploadSelector : null
          }
          {
            (!this.props.isEditMode || (this.props.isEditMode && this.state.isActionRequired)) ?
              consentLine : null
          }
        </div>
      </div>
    );
  }
}
